import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ExternalLink, FileX, X } from 'lucide-react';
import { drugsService } from '@/services/drugs';
import { LoadingState } from '@/components/ui';
import DOMPurify from 'dompurify';

interface DrugNoticeModalProps {
  drugId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

export function DrugNoticeModal({ drugId, isOpen, onClose }: DrugNoticeModalProps) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['drug-notice', drugId],
    queryFn: () => drugsService.getNotice(drugId!),
    enabled: isOpen && !!drugId,
  });

  // Escape to close + lock background scroll while open.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const fallbackUrl = data?.noticeUrl
    || (data?.tradeName ? `https://www.google.com/search?q=notice+medicament+${encodeURIComponent(data.tradeName)}` : null);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="drug-notice-title"
        className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-xl"
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between gap-4 border-b border-gray-100 px-5 py-4">
          <h2 id="drug-notice-title" className="card-title truncate">
            {data?.tradeName ? t('drugNotice.titleWithName', { name: data.tradeName }) : t('drugNotice.title')}
          </h2>
          <button type="button" onClick={onClose} className="btn-icon -me-2 shrink-0" aria-label={t('drugNotice.close')}>
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {isLoading && <LoadingState label={t('drugNotice.loading')} className="py-12" />}

          {isError && (
            <div className="alert-danger" role="alert">{t('drugNotice.error')}</div>
          )}

          {!isLoading && !isError && data?.noticeContent && (
            <div
              className="drug-notice-content max-w-none break-words text-gray-800
                [&_h1]:text-lg [&_h1]:font-bold [&_h1]:mt-4 [&_h1]:mb-2
                [&_h2]:text-base [&_h2]:font-bold [&_h2]:mt-3 [&_h2]:mb-2
                [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1
                [&_p]:text-sm [&_p]:leading-relaxed [&_p]:mb-2
                [&_ul]:list-disc [&_ul]:ps-5 [&_ul]:mb-2
                [&_ol]:list-decimal [&_ol]:ps-5 [&_ol]:mb-2
                [&_li]:text-sm [&_li]:mb-1
                [&_table]:block [&_table]:w-full [&_table]:overflow-x-auto [&_table]:text-sm [&_table]:border-collapse
                [&_td]:border [&_td]:border-gray-200 [&_td]:p-1
                [&_th]:border [&_th]:border-gray-200 [&_th]:p-1 [&_th]:bg-gray-50
                [&_img]:max-w-full [&_img]:h-auto
                [&_a]:text-primary-700 [&_a]:underline"
              // Notice HTML is scraped from an external source: sanitize before injecting.
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.noticeContent) }}
            />
          )}

          {!isLoading && !isError && !data?.noticeContent && (
            <div className="flex flex-col items-center px-4 py-12 text-center">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                <FileX className="h-6 w-6 text-gray-400" />
              </div>
              <p className="mb-4 text-sm text-gray-500">{t('drugNotice.unavailable')}</p>
              {fallbackUrl && (
                <a
                  href={fallbackUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline btn-sm"
                >
                  <ExternalLink className="h-4 w-4" />
                  {t('drugNotice.viewOnline')}
                </a>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-gray-100 px-5 py-3">
          {fallbackUrl && data?.noticeContent && (
            <a
              href={fallbackUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="link inline-flex items-center gap-1 text-sm"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              {t('drugNotice.viewSource')}
            </a>
          )}
          <button type="button" onClick={onClose} className="btn-secondary ms-auto">
            {t('drugNotice.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
